import type { PaymentMethod } from '@/types/checkout';
import { PaymentMethodTypeToEnum } from '@/constants/paymentMethodTypeMapping';
import type { PaymentMethodEnum } from '@/types/common';
import type { BootCheckoutConfiguration } from '@/types/boot';

/**
 * Read the configured `availablePaymentMethods` allow-list (declared as
 * `PaymentMethodType[]`) and translate it into the runtime `PaymentMethodEnum[]`
 * the rest of the checkout works with. Unknown types are dropped.
 *
 * Source-of-truth note (validated 2026-05-21 against staging BE):
 * The BE delivers this allow-list on the **boot JWT**
 * (`bootData.checkoutConfiguration.availablePaymentMethods`, singular),
 * NOT on `/checkout/v3` (`checkoutData.checkoutConfigurations.availablePaymentMethods`,
 * plural — that field is never populated). Callers must therefore pass the
 * boot's `checkoutConfiguration`, not the `/checkout/v3` `checkoutConfigurations`.
 */
export const getConfiguredPaymentMethodEnums = (config?: BootCheckoutConfiguration): PaymentMethodEnum[] => {
  const types = config?.availablePaymentMethods ?? [];
  return types.map((type) => PaymentMethodTypeToEnum[type]).filter(Boolean) as PaymentMethodEnum[];
};

/**
 * Applies the boot-level `checkoutConfiguration.availablePaymentMethods`
 * allow-list to the BE-provided payment methods. When the allow-list is
 * empty/missing every method is kept, so templates without an allow-list
 * see the full BE response.
 */
export const filterByConfiguredAvailablePaymentMethods = (
  paymentMethods: PaymentMethod[],
  config?: BootCheckoutConfiguration
): PaymentMethod[] => {
  const allowed = getConfiguredPaymentMethodEnums(config);
  if (allowed.length === 0) return paymentMethods;
  return paymentMethods.filter((pm) => allowed.includes(pm.paymentMethodName as PaymentMethodEnum));
};
